import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, CheckCheck } from "lucide-react";

export function NotificationPanel() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.count);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleClick = async (notif) => {
    if (!notif.read) {
      try { await api.put(`/notifications/${notif.id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    navigate(`/requests/${notif.request_id}`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      fetchNotifications();
    } catch { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 p-0"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5 text-[#525C58]" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1"
              data-testid="notification-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b border-[#E8E8E6]">
          <h3 className="font-medium text-[#1C1F1E] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              className="text-xs text-[#40916C] hover:text-[#1B4332] h-7"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="text-center py-12" data-testid="no-notifications">
              <Bell className="h-8 w-8 mx-auto mb-2 text-[#E8E8E6]" />
              <p className="text-[#7A8A84] text-sm">No notifications</p>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                className={`p-4 border-b border-[#E8E8E6] cursor-pointer hover:bg-[#F4F4F1] transition-colors duration-150 ${
                  !n.read ? "bg-emerald-50/30" : ""
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <div className="w-2 h-2 bg-[#40916C] rounded-full mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#1C1F1E] truncate">{n.title}</p>
                    <p className="text-xs text-[#525C58] mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-xs text-[#7A8A84] mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
